import { addDays, addMonths, differenceInCalendarDays, isPast, isToday } from 'date-fns'
import type { DoseDefinition, ScheduleRuleV1, VaccinationRecord } from '../types'

export interface ScheduleRuleContext {
  birthDate: Date
  doseDates: Map<string, Date>
}

function doseKey(vaccineId: string, doseIndex: number): string {
  return `${vaccineId}-${doseIndex}`
}

function isInsideSeason(date: Date, rule: NonNullable<ScheduleRuleV1['alignTo']>): boolean {
  const value = (date.getMonth() + 1) * 100 + date.getDate()
  const start = rule.startMonth * 100 + rule.startDay
  const end = rule.endMonth * 100 + rule.endDay
  return start <= end ? value >= start && value <= end : value >= start || value <= end
}

function nextSeasonStart(date: Date, rule: NonNullable<ScheduleRuleV1['alignTo']>): Date {
  const thisYearStart = new Date(date.getFullYear(), rule.startMonth - 1, rule.startDay)
  return date <= thisYearStart ? thisYearStart : new Date(date.getFullYear() + 1, rule.startMonth - 1, rule.startDay)
}

export function calculateDateFromScheduleRule(
  dose: DoseDefinition,
  context: ScheduleRuleContext
): Date | null {
  const rule = dose.scheduleRule
  if (!rule) return null
  if (rule.version !== 1 || rule.type !== 'anchorOffset') {
    console.warn(`[VaccineRules] 不支援 ${dose.id} 的規則版本，改用 legacy 欄位`)
    return null
  }

  let result: Date | undefined
  if (rule.anchor.type === 'birth') {
    result = new Date(context.birthDate)
  } else {
    result = context.doseDates.get(
      doseKey(rule.anchor.vaccineId ?? dose.vaccineId, rule.anchor.doseIndex)
    )
  }
  if (!result) return null

  if (rule.offset?.months != null) result = addMonths(result, rule.offset.months)
  if (rule.offset?.days != null) result = addDays(result, rule.offset.days)

  if (rule.alignTo?.type === 'seasonWindow' && !isInsideSeason(result, rule.alignTo)) {
    result = nextSeasonStart(result, rule.alignTo)
  }

  return result
}

export function getScheduleDependency(dose: DoseDefinition): { vaccineId: string; doseIndex: number } | null {
  if (dose.scheduleRule?.version === 1 && dose.scheduleRule.anchor.type === 'dose') {
    return {
      vaccineId: dose.scheduleRule.anchor.vaccineId ?? dose.vaccineId,
      doseIndex: dose.scheduleRule.anchor.doseIndex,
    }
  }
  if (dose.intervalFromDose) {
    return { vaccineId: dose.vaccineId, doseIndex: dose.intervalFromDose.fromDoseIndex }
  }
  return null
}

/* =========================================================
   接種年齡上限（age cap）
   來源：https://www.cdc.gov.tw/Category/Page/Swi2NuzkthETRWhmAmFOnw（查核日期 2026-08-25）
   「2劑時程：最後1劑不得晚於出生後24週接種」
   「3劑時程：最後1劑不得晚於出生後32週接種」
========================================================= */

const ROTA_2DOSE_MAX_AGE_DAYS = 24 * 7 // 168 天
const ROTA_3DOSE_MAX_AGE_DAYS = 32 * 7 // 224 天

/**
 * 判斷輪狀病毒第1劑是否已確認為「2劑型」：
 * 第3劑因互斥規則被自動 skip（skippedByDoseIds 有來源）即代表使用者於第1劑確認接種時選了 2劑型。
 */
function isRota2DoseType(siblingRecords: VaccinationRecord[]): boolean {
  const dose3 = siblingRecords.find(r => r.vaccineId === 'rota' && r.doseIndex === 3)
  return dose3?.status === 'skipped' && (dose3.skippedByDoseIds?.length ?? 0) > 0
}

/**
 * 取得某劑次的接種年齡上限日期（超過此日期仍未接種時應轉為 expired）。
 * 目前僅輪狀病毒（依 2劑型/3劑型動態判斷）與帶有 maxAgeFromBirth 的劑次（如 menB 追加劑）有上限。
 * 其餘疫苗回傳 null，代表沒有已查核的年齡上限，行為與過去相同（overdue 不會轉 expired）。
 */
export function getAgeCapDate(
  dose: DoseDefinition,
  birthDate: Date,
  siblingRecords: VaccinationRecord[]
): Date | null {
  if (dose.vaccineId === 'rota') {
    if (dose.doseIndex === 2) {
      return isRota2DoseType(siblingRecords) ? addDays(birthDate, ROTA_2DOSE_MAX_AGE_DAYS) : null
    }
    if (dose.doseIndex === 3) {
      return addDays(birthDate, ROTA_3DOSE_MAX_AGE_DAYS)
    }
    return null
  }

  if (dose.maxAgeFromBirth) {
    let capDate = birthDate
    if (dose.maxAgeFromBirth.months != null) capDate = addMonths(capDate, dose.maxAgeFromBirth.months)
    if (dose.maxAgeFromBirth.days != null) capDate = addDays(capDate, dose.maxAgeFromBirth.days)
    return capDate
  }

  return null
}

/**
 * 統一入口：計算「非已完成/已跳過」記錄應有的狀態（overdue / planned / expired）。
 * 取代原本散落各處的 `isPast(d) && !isToday(d) ? 'overdue' : 'planned'`，
 * 差異僅在於：若該劑次有查核過的年齡上限且已超過，回傳 expired。
 */
export function resolvePlannedStatus(
  plannedDate: Date,
  ageCapDate: Date | null
): 'overdue' | 'planned' | 'expired' {
  if (ageCapDate && isPast(ageCapDate) && !isToday(ageCapDate)) return 'expired'
  return isPast(plannedDate) && !isToday(plannedDate) ? 'overdue' : 'planned'
}

/* =========================================================
   最小接種間隔引擎（min-interval engine）
   建議日期 = 「官方常規時程」（scheduleRule / intervalFromBirth / intervalFromDose 等既有邏輯）
             與「所有適用的 minIntervals floor」兩者取最晚。
   純函式，供 useAppStore.ts 的四個排程入口共用，避免各自重算邏輯漂移。
========================================================= */

function getFluSeasonYear(date: Date): number {
  const month = date.getMonth() + 1
  if (month >= 10) return date.getFullYear()
  if (month <= 2) return date.getFullYear() - 1
  return date.getFullYear()
}

export function calculateFluPlannedDate(birthDate: Date, doseIndex: number): Date | null {
  if (doseIndex % 2 === 0) return null

  const sixMonthDate = addMonths(birthDate, 6)
  const month = sixMonthDate.getMonth() + 1

  let y1Date: Date
  if (month >= 10 || month <= 2) {
    y1Date = sixMonthDate
  } else {
    y1Date = new Date(sixMonthDate.getFullYear(), 9, 1)
  }

  if (doseIndex === 1) return y1Date

  const seasonYear = getFluSeasonYear(y1Date)
  const fluYear = (doseIndex + 1) / 2
  return new Date(seasonYear + fluYear - 1, 9, 1)
}

export function calculateBeyfortusDate(birthDate: string, motherVaccinated: boolean): Date {
  const birth = new Date(birthDate)
  const today = new Date()

  const isRSVSeason = (d: Date): boolean => {
    const m = d.getMonth()
    return m >= 9 || m <= 2
  }

  const nextRSVStart = (from: Date): Date => {
    if (isRSVSeason(from)) return new Date(from)
    return new Date(from.getFullYear(), 9, 1) // 當年 10/1
  }

  if (motherVaccinated) {
    const earliest = addMonths(birth, 3)
    return isRSVSeason(earliest) ? earliest : nextRSVStart(earliest)
  } else {
    const refDate = today > birth ? today : birth
    return nextRSVStart(refDate)
  }
}

export function calculateBeyfortusRedoseDate(previousDate: Date, minIntervalMonths: number): Date {
  const earliest = addMonths(previousDate, minIntervalMonths)
  const month = earliest.getMonth()
  if (month >= 9 || month <= 2) return earliest
  return new Date(earliest.getFullYear(), 9, 1)
}

export interface CandidateDateContext {
  birthDate: Date
  doseDates: Map<string, Date> // key: `${vaccineId}-${doseIndex}`，值為該劑次目前已知的有效日期
  allDoses: DoseDefinition[]
  maternalRSVVaccinated: boolean
}

/**
 * 計算某劑次的「官方常規時程」候選日期（尚未套用 minIntervals floor）。
 * 依序比對：流感季節對齊 → RSV 首次給藥 → scheduleRule → RSV 再次給藥 →
 * intervalFromBirth → intervalFromDose（含依賴劑次尚無紀錄時的估算 fallback）。
 * 與過去散落在 generateVaccinationRecords / createMissingRecordsForExistingBabies /
 * updateVaccineRecordsToNewVersion / recalculateSubsequentDoses 四處的判斷邏輯等價。
 */
export function resolveCandidateDate(dose: DoseDefinition, context: CandidateDateContext): Date | null {
  const { birthDate, doseDates, allDoses, maternalRSVVaccinated } = context

  if (dose.vaccineId === 'flu' && dose.intervalFromBirth) {
    return calculateFluPlannedDate(birthDate, dose.doseIndex)
  }

  if (dose.flexibleTiming && dose.vaccineId === 'rsv' && dose.doseIndex === 1) {
    return calculateBeyfortusDate(birthDate.toISOString().slice(0, 10), maternalRSVVaccinated)
  }

  if (dose.scheduleRule) {
    return calculateDateFromScheduleRule(dose, { birthDate, doseDates })
  }

  if (dose.vaccineId === 'rsv' && dose.intervalFromDose) {
    const baseDate = doseDates.get(doseKey(dose.vaccineId, dose.intervalFromDose.fromDoseIndex))
    return baseDate ? calculateBeyfortusRedoseDate(baseDate, dose.intervalFromDose.months ?? 6) : null
  }

  if (dose.intervalFromBirth?.months != null) return addMonths(birthDate, dose.intervalFromBirth.months)
  if (dose.intervalFromBirth?.days != null) return addDays(birthDate, dose.intervalFromBirth.days)

  if (dose.intervalFromDose) {
    const baseDate = doseDates.get(doseKey(dose.vaccineId, dose.intervalFromDose.fromDoseIndex))
    if (baseDate) {
      if (dose.intervalFromDose.months != null) return addMonths(baseDate, dose.intervalFromDose.months)
      if (dose.intervalFromDose.gapDays != null) return addDays(baseDate, dose.intervalFromDose.gapDays)
      return null
    }

    // 依賴的劑次還沒有紀錄：用它自己的 intervalFromBirth 估算基準日期
    const baseDose = allDoses.find(
      d => d.doseIndex === dose.intervalFromDose!.fromDoseIndex && d.vaccineId === dose.vaccineId
    )
    if (baseDose) {
      let estimatedBaseDate: Date | null = null
      if (baseDose.intervalFromBirth?.months != null) estimatedBaseDate = addMonths(birthDate, baseDose.intervalFromBirth.months)
      else if (baseDose.intervalFromBirth?.days != null) estimatedBaseDate = addDays(birthDate, baseDose.intervalFromBirth.days)

      if (estimatedBaseDate) {
        if (dose.intervalFromDose.months != null) return addMonths(estimatedBaseDate, dose.intervalFromDose.months)
        if (dose.intervalFromDose.gapDays != null) return addDays(estimatedBaseDate, dose.intervalFromDose.gapDays)
      }
    }
  }

  return null
}

/**
 * 套用 minIntervals floor：對每一條有前一劑「有效日期」可查的規則，
 * 算出 floor = 前一劑有效日期 + 間隔天數（依 conditionalOverrides 決定實際天數），
 * 最終回傳 candidateDate 與所有 floor 的最大值。缺前劑資料的規則直接忽略，不影響其他規則。
 */
function resolveMinIntervalRuleGapDays(
  rule: NonNullable<DoseDefinition['minIntervals']>[number],
  ageAtPrevDoseDays: number
): number {
  const override = rule.conditionalOverrides
    ?.slice()
    .sort((a, b) => b.ifAgeAtPreviousDoseAtLeastMonths - a.ifAgeAtPreviousDoseAtLeastMonths)
    .find(o => ageAtPrevDoseDays >= o.ifAgeAtPreviousDoseAtLeastMonths * 30)
  return override?.days ?? rule.days
}

/**
 * 算出 minIntervals 陣列中所有規則的 floor 日期，取最晚者。缺前劑資料的規則直接忽略。
 * 回傳 null 表示沒有任何一條規則有資料可算（不同於 applyMinIntervalFloors 會退回 candidateDate，
 * 這裡是給 getEarliestValidVaccinationDate 共用的內部計算，null 代表「這個面向沒有下限」）。
 */
function resolveMinIntervalFloorDate(
  dose: DoseDefinition,
  birthDate: Date,
  effectiveDatesByDoseId: Map<string, Date>
): Date | null {
  if (!dose.minIntervals || dose.minIntervals.length === 0) return null

  let result: Date | null = null
  for (const rule of dose.minIntervals) {
    const prevDate = effectiveDatesByDoseId.get(rule.fromDoseId)
    if (!prevDate) continue

    const ageAtPrevDoseDays = differenceInCalendarDays(prevDate, birthDate)
    const gapDays = resolveMinIntervalRuleGapDays(rule, ageAtPrevDoseDays)
    const floor = addDays(prevDate, gapDays)
    if (!result || floor > result) result = floor
  }

  return result
}

export function applyMinIntervalFloors(
  dose: DoseDefinition,
  candidateDate: Date,
  birthDate: Date,
  effectiveDatesByDoseId: Map<string, Date>
): Date {
  const floor = resolveMinIntervalFloorDate(dose, birthDate, effectiveDatesByDoseId)
  return floor && floor > candidateDate ? floor : candidateDate
}

/* =========================================================
   提前接種視同無效規則（early-vaccination grace period）
   來源：疾管署《現行預防接種時程表與間隔補種指引》114.4版 附註9（查核日期 2026-08-25）
   「表列同種類疫苗(水痘、MMR及JE活性減毒疫苗除外)各劑次，因特殊情況可容許最短接種間隔
   或最小接種年齡再提前4天之寬限期，若提前5天(含)以上接種者，則該劑疫苗應視同無效，
   並依表列接種年齡、間隔規範重新安排接種。」(依據ACIP 108年第3次會議決議修訂)
   目前僅套用在此文件查核範圍內、且已建好 minAgeFromBirth/minIntervals 資料的疫苗
   （HepB、五合一、PCV13、HepA）。水痘/MMR/JE活性減毒為文件明文排除；
   輪狀、PCV15/20、menB、六合一等不在此文件查核範圍內，暫不套用，避免套用未查證來源的門檻。
   僅供提示，不阻擋確認接種——使用者可能有醫師評估等特殊情況，App 沒有資格逕自認定無效。
========================================================= */

const EARLY_VACCINATION_GRACE_DAYS = 4
const EARLY_VACCINATION_APPLICABLE_VACCINE_IDS = new Set(['hepB', '5in1', 'pcv13', 'hepA'])

/**
 * 計算某劑次「提前接種仍視為有效」的最早日期（已扣除4天寬限）。
 * 取 minAgeFromBirth（第1劑最小接種年齡）與 minIntervals floor（與前一劑的最短間隔）兩者較晚者，再減4天。
 * 回傳 null 表示此劑次不適用本規則（疫苗不在查核範圍內，或沒有任何可用的下限資料）。
 */
export function getEarliestValidVaccinationDate(
  dose: DoseDefinition,
  birthDate: Date,
  effectiveDatesByDoseId: Map<string, Date>
): Date | null {
  if (!EARLY_VACCINATION_APPLICABLE_VACCINE_IDS.has(dose.vaccineId)) return null

  let threshold: Date | null = null

  if (dose.minAgeFromBirth) {
    let ageDate = birthDate
    if (dose.minAgeFromBirth.months != null) ageDate = addMonths(ageDate, dose.minAgeFromBirth.months)
    if (dose.minAgeFromBirth.days != null) ageDate = addDays(ageDate, dose.minAgeFromBirth.days)
    threshold = ageDate
  }

  const intervalFloor = resolveMinIntervalFloorDate(dose, birthDate, effectiveDatesByDoseId)
  if (intervalFloor && (!threshold || intervalFloor > threshold)) threshold = intervalFloor

  if (!threshold) return null
  return addDays(threshold, -EARLY_VACCINATION_GRACE_DAYS)
}

/**
 * 判斷某個實際接種日期是否提前超過寬限期，依規定可能視同無效需補種。僅供 UI 顯示提示用。
 */
export function isVaccinationTooEarly(
  dose: DoseDefinition,
  actualDate: Date,
  birthDate: Date,
  effectiveDatesByDoseId: Map<string, Date>
): boolean {
  const earliestValid = getEarliestValidVaccinationDate(dose, birthDate, effectiveDatesByDoseId)
  if (!earliestValid) return false
  return actualDate.getTime() < earliestValid.getTime()
}
